import React, { useRef, useState } from 'react';
import { Swiper as SwiperModule, SwiperSlide } from 'swiper/react';
import Swiper, { FreeMode, Navigation, Thumbs } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/thumbs';
import { createDataLayerString } from '../../helpers/helpers';
import { BREAKPOINTS } from '../../helpers/breakpoints';

type ImageGalleryProps = {
  images: {
    media_type: string;
    media_url: string;
    alt_text: string;
  }[];
  nextLabel: string;
  prevLabel: string;
  productHeading: string;
  category: string;
};

export const ImageGallery = ({
  images,
  nextLabel,
  prevLabel,
  productHeading,
  category
}: ImageGalleryProps) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);
  const [swiper, setSwiper] = useState<Swiper | null>(null);
  const nextButton = useRef(null);
  const backButton = useRef(null);

  const handleThumbsUpdate = () => {
    if (swiper?.realIndex) {
      thumbsSwiper?.slideTo(swiper.realIndex);
    }
  };

  return (
    <>
      <div className="product-masthead__image">
        <SwiperModule
          spaceBetween={10}
          navigation={{
            nextEl: nextButton.current,
            prevEl: backButton.current
          }}
          onSwiper={swiper => setSwiper(swiper)}
          onSlideChange={() => {
            handleThumbsUpdate();
          }}
          thumbs={{ swiper: thumbsSwiper }}
          modules={[FreeMode, Navigation, Thumbs]}
        >
          {images.map((image, index) => (
            <SwiperSlide key={index}>
              <picture>
                <source
                  srcSet={`${image.media_url}?width=616`}
                  media={`(min-width: ${BREAKPOINTS.desktop - 1}px)`}
                />
                <source
                  srcSet={`${image.media_url}?width=558`}
                  media={`(min-width: ${BREAKPOINTS.tablet - 1}px)`}
                />
                <img
                  src={`${image.media_url}?width=686`}
                  alt={image.alt_text}
                  width="686"
                  height="686"
                />
              </picture>
            </SwiperSlide>
          ))}
        </SwiperModule>
        <button
          ref={backButton}
          className="swiper-button-prev"
          aria-label={prevLabel || 'previous slide'}
          title={prevLabel || 'previous slide'}
          data-layer={createDataLayerString([
            {
              event: 'product_click',
              event_category: 'product',
              event_action: 'gallery',
              event_label: 'previous arrow',
              event_attributes: {
                product_name: productHeading,
                product_category: category
              }
            }
          ])}
        ></button>

        <button
          ref={nextButton}
          className="swiper-button-next"
          aria-label={nextLabel || 'next slide'}
          title={nextLabel || 'next slide'}
          data-layer={createDataLayerString([
            {
              event: 'product_click',
              event_category: 'product',
              event_action: 'gallery',
              event_label: 'next arrow',
              event_attributes: {
                product_name: productHeading,
                product_category: category
              }
            }
          ])}
        ></button>
      </div>
      <div className="product-masthead__thumbs-wrapper">
        <SwiperModule
          onSwiper={swiper => setThumbsSwiper(swiper)}
          initialSlide={0}
          slidesPerGroup={1}
          spaceBetween={20}
          slidesPerView={4}
          freeMode={true}
          watchSlidesProgress={true}
          modules={[FreeMode, Navigation, Thumbs]}
          className="product-masthead__thumbs"
        >
          {images.map((image, index) => (
            <SwiperSlide key={index}>
              <button
                onClick={() => swiper?.slideTo(index)}
                data-layer={createDataLayerString([
                  {
                    event: 'product_click',
                    event_category: 'product',
                    event_action: 'gallery',
                    event_label: 'thumbnail',
                    event_attributes: {
                      product_name: productHeading,
                      product_category: 'Shingle'
                    }
                  }
                ])}
              >
                <picture>
                  <source
                    srcSet={`${image.media_url}?width=137`}
                    media={`(min-width: ${BREAKPOINTS.wide - 1}px)`}
                  />
                  <source
                    srcSet={`${image.media_url}?width=122`}
                    media={`(min-width: ${BREAKPOINTS.tabletLandscape - 1}px)`}
                  />
                  <source
                    srcSet={`${image.media_url}?width=98`}
                    media={`(min-width: ${BREAKPOINTS.tablet - 1}px)`}
                  />
                  <img
                    src={`${image.media_url}?width=165`}
                    alt={image.alt_text}
                    width="165"
                    height="165"
                    loading="lazy"
                  />
                </picture>
              </button>
            </SwiperSlide>
          ))}
        </SwiperModule>
      </div>
    </>
  );
};

export default ImageGallery;
